import { Product } from '@/interfaces/catalog/Product'
import { Sku } from '@/interfaces/catalog/Sku'
import { StockItem } from '@/interfaces/warehouse/StockItem'

/*
 * API vrací u produktu a SKU víc polí, než popisuje schema.d.ts
 * (variantní osy, parametry, obrázky). Tady jsou dopsané typy,
 * dokud se schéma nepřegeneruje.
 */

export interface SkuImage {
  id: string
  url: string
  sortOrder: number
}

export interface SkuOption {
  axisId: string
  valueId: string
  label: string
}

export type CatalogSku = Sku & {
  options?: SkuOption[]
  images?: SkuImage[]
  /** Vlastní obrázky SKU, nebo obrázky produktu (systémového SKU), když SKU žádné nemá. */
  gallery?: SkuImage[]
}

export interface OptionValue {
  id: string
  code: string
  label: string
  sortOrder: number
}

export interface ProductVariantAxis {
  axisId: string
  sortOrder: number
  axis: {
    id: string
    code: string
    name: string
    valueType: string
    values: OptionValue[]
  }
}

export interface ProductParameter {
  parameterId: string
  sortOrder: number
  isRequired: boolean
  parameter: {
    id: string
    code: string
    name: string
    valueType: string
    values: OptionValue[]
  }
}

export interface DescriptiveValue {
  parameterId: string
  parameterValueId: string | null
  rawValue: string | null
}

export type CatalogProduct = Product & {
  variantAxes?: ProductVariantAxis[]
  parameters?: ProductParameter[]
  descriptiveValues?: DescriptiveValue[]
}

export const productTypeLabel: Record<string, string> = {
  physical: 'Fyzický',
  virtual: 'Virtuální',
  service: 'Služba',
  package: 'Balíček',
}

/** Produkt, ke kterému patří zásoba. Systémové SKU mají v `skuCode` přímo id produktu. */
export const productIdOfStockItem = (
  item: Pick<StockItem, 'skuId' | 'skuCode'>,
  productIdBySkuId: Map<string, string>,
): string | undefined =>
  productIdBySkuId.get(item.skuId) ?? (item.skuCode?.startsWith('pr_') ? item.skuCode : undefined)

export interface StockSummary {
  /** Součet dostupného množství sledovaných zásob */
  available: number
  onHand: number
  reserved: number
  /** Aspoň jedna zásoba je neomezená */
  unlimited: boolean
  /** Aspoň jedna zásoba se sleduje */
  tracked: boolean
}

export const emptyStock = (): StockSummary => ({ available: 0, onHand: 0, reserved: 0, unlimited: false, tracked: false })

export const addToStock = (summary: StockSummary, item: StockItem) => {
  if (item.isUnlimited) {
    summary.unlimited = true
    return
  }
  summary.tracked = true
  summary.available += item.quantityAvailable
  summary.onHand += item.quantityOnHand
  summary.reserved += item.quantityReserved
}

export interface ProductSummary {
  variantCount: number
  minPrice: number | null
  maxPrice: number | null
  currency: string
  thumbnail?: string
  stock: StockSummary | null
}

/** Souhrn pro řádek seznamu produktů: počet variant, rozpětí cen, sklad a náhled. */
export const buildProductSummaries = (
  skus: CatalogSku[],
  stockItems: StockItem[] | null,
): Map<string, ProductSummary> => {
  const map = new Map<string, ProductSummary>()
  const productIdBySkuId = new Map<string, string>()

  for (const sku of skus) {
    productIdBySkuId.set(sku.id, sku.productId)
    const summary = map.get(sku.productId) ?? {
      variantCount: 0,
      minPrice: null,
      maxPrice: null,
      currency: 'CZK',
      stock: null,
    }
    if (!sku.isSystem) summary.variantCount += 1
    const amount = sku.price?.price?.amount
    if (amount !== undefined && amount !== null && amount !== '') {
      const value = Number(amount)
      summary.minPrice = summary.minPrice === null ? value : Math.min(summary.minPrice, value)
      summary.maxPrice = summary.maxPrice === null ? value : Math.max(summary.maxPrice, value)
      summary.currency = sku.price?.price?.currency ?? summary.currency
    }
    if (!summary.thumbnail) summary.thumbnail = sku.gallery?.[0]?.url ?? sku.images?.[0]?.url
    map.set(sku.productId, summary)
  }

  if (stockItems) {
    for (const item of stockItems) {
      const productId = productIdOfStockItem(item, productIdBySkuId)
      if (!productId) continue
      const summary = map.get(productId)
      if (!summary) continue
      summary.stock ??= emptyStock()
      addToStock(summary.stock, item)
    }
  }

  return map
}

/** Popisek varianty: hodnoty os ("250 g"), jinak název SKU bez názvu produktu. */
export const variantLabel = (sku: CatalogSku, productName?: string) => {
  if (sku.isSystem) return 'Výchozí varianta'
  if (sku.options?.length) return sku.options.map((o) => o.label).join(' / ')
  if (productName && sku.name.startsWith(productName)) {
    const rest = sku.name.slice(productName.length).replace(/^[\s,-]+/, '')
    if (rest) return rest
  }
  return sku.name
}

/** Převod částky zadané v korunách ("309,50") na haléře. */
export const parseMajorAmount = (value: string): number | null => {
  const normalized = value.replace(/\s/g, '').replace(',', '.')
  if (!normalized) return null
  const number = Number(normalized)
  if (!Number.isFinite(number) || number < 0) return null
  return Math.round(number * 100)
}

export const formatCount = (value: number, one: string, few: string, many: string) => {
  const abs = Math.abs(value)
  const word = abs === 1 ? one : abs >= 2 && abs <= 4 ? few : many
  return `${value.toLocaleString('cs-CZ')} ${word}`
}
